import React, { useMemo, useState, useEffect,useCallback,useRef } from "react";
import { FaCalendarAlt } from "react-icons/fa";
import { FaLock, FaUnlock } from "react-icons/fa";

import { API_BASE } from "../config";

/* ===========================
   ✅ NOUVEAU : BLOCAGES
=========================== */

export function BlocagesMedecin({ user }) {
  const token = user?.token;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({ type: "", msg: "" });
  
  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast({ type: "", msg: "" }), 3200);
  };

  // -------------------- Date helpers --------------------
  const todayYYYYMMDD = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatLongFr = (yyyyMMdd) => {
    const d = new Date(`${yyyyMMdd}T00:00:00`);
    if (Number.isNaN(d.getTime())) return yyyyMMdd;
    return d.toLocaleDateString("fr-FR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "2-digit",
    });
  };

  // ✅ date par défaut = dernière date consultée, sinon aujourd’hui
  const [date, setDate] = useState(() => {
    return localStorage.getItem("blocages_selected_date") || todayYYYYMMDD();
  });

  useEffect(() => {
    localStorage.setItem("blocages_selected_date", date);
  }, [date]);

  const startOfTodayMs = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };

  const endOfTodayMs = () => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d.getTime();
  };

  // -------------------- Headers --------------------
  const headers = useMemo(
    () => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${token || ""}`,
    }),
    [token]
  );



  // -------------------- Formatting --------------------
  

  // -------------------- Data --------------------
  const [dispos, setDispos] = useState([]);
  const [blocagesJour, setBlocagesJour] = useState([]);
  const [allBlocages, setAllBlocages] = useState([]);

  // -------------------- Fetchers (stable => no ESLint warnings) --------------------
  const fetchDispos = useCallback(async () => {
    if (!token) return;
    const res = await fetch(`${API_BASE}/medecin/disponibilites`, { headers });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.msg || "Erreur chargement disponibilités");
    setDispos(data.items || []);
  }, [token, headers]);

  const fetchAllBlocages = useCallback(async () => {
    if (!token) return;
    const res = await fetch(`${API_BASE}/medecin/blocages`, { headers });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.msg || "Erreur chargement blocages");
    setAllBlocages(data.items || []);
  }, [token, headers]);

  const fetchBlocagesJour = useCallback(
    async (day) => {
      if (!token) return;
      const res = await fetch(`${API_BASE}/medecin/blocages/jour?date=${day}`, { headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || "Erreur chargement blocages du jour");
      setBlocagesJour(data.items || []);
    },
    [token, headers]
  );

  // -------------------- Effects --------------------
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {

        await fetchDispos();
        await fetchAllBlocages();
        await fetchBlocagesJour(date);
      } catch (e) {
        if (mounted) setError(e?.message || "Impossible de contacter le serveur.");
      }
    })();
    return () => {
      mounted = false;
    };
  }, [date, fetchAllBlocages, fetchBlocagesJour, fetchDispos]);

  // -------------------- Blocages: aujourd’hui / à venir --------------------
  const todayBlocages = useMemo(() => {
    const start = startOfTodayMs();
    const end = endOfTodayMs();

    return (allBlocages || [])
      .map((b) => ({ ...b, s: new Date(b.debut).getTime(), e: new Date(b.fin).getTime() }))
      .filter((b) => !Number.isNaN(b.s) && !Number.isNaN(b.e) && b.s <= end && b.e >= start)
      .sort((a, b) => a.s - b.s);
  }, [allBlocages]);

  const upcomingBlocages = useMemo(() => {
    const end = endOfTodayMs();
    return (allBlocages || [])
      .map((b) => ({ ...b, s: new Date(b.debut).getTime(), e: new Date(b.fin).getTime() }))
      .filter((b) => !Number.isNaN(b.s) && b.s > end)
      .sort((a, b) => a.s - b.s)
      .slice(0, 8);
  }, [allBlocages]);

  // -------------------- Slots generation (from weekly dispos) --------------------
  const timeToMin = (t) => {
    const [h, m] = (t || "").split(":").map(Number);
    return (h || 0) * 60 + (m || 0);
  };

  const minToHHMM = (m) =>
    `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

  const weekday = (yyyyMmDd) => {
    const js = new Date(`${yyyyMmDd}T00:00:00`).getDay();
    return js === 0 ? 7 : js;
  };

  const slots = useMemo(() => {
    const jour = weekday(date);
    const out = [];

    const plages = (dispos || [])
      .filter((d) => Number(d.jour_semaine) === Number(jour))
      .slice()
      .sort((a, b) => timeToMin(a.heure_debut) - timeToMin(b.heure_debut));

    for (const p of plages) {
      const start = timeToMin(p.heure_debut);
      const end = timeToMin(p.heure_fin);
      const step = Number(p.duree_consultation || 30);
      if (!step || step < 5) continue;

      for (let t = start; t + step <= end; t += step) {
        const startHHMM = minToHHMM(t);
        const endHHMM = minToHHMM(t + step);
        const toParisUTCISOString = (yyyyMMdd, hhmm) => {
          const [y, m, d] = yyyyMMdd.split("-").map(Number);
          const [h, min] = hhmm.split(":").map(Number);
        
          // On crée la date en UTC d'abord
          const utcDate = new Date(Date.UTC(y, m - 1, d, h, min));
        
          // On calcule le vrai offset Paris à cette date (gère été/hiver)
          const parisOffsetMinutes = -utcDate.toLocaleString("en-US", {
            timeZone: "Europe/Paris",
            timeZoneName: "short"
          }).includes("GMT+2") ? 120 : 60;
        
          // Ajuster vers UTC réel
          utcDate.setUTCMinutes(utcDate.getUTCMinutes() - parisOffsetMinutes);
        
          return utcDate.toISOString();
        };
        
        const debutIso = toParisUTCISOString(date, startHHMM);
        const finIso   = toParisUTCISOString(date, endHHMM);
        

        

        const blocked = (blocagesJour || []).some((b) => {
          const bs = new Date(b.debut).getTime();
          const be = new Date(b.fin).getTime();
          const ss = new Date(debutIso).getTime();
          const se = new Date(finIso).getTime();
          return ss < be && bs < se;
        });

        out.push({ key: debutIso, debutIso, finIso, label: `${startHHMM} → ${endHHMM}`, blocked });
      }
    }
    return out;
  }, [blocagesJour, date, dispos]);

  // -------------------- Toggle (POST /blocages/toggle) --------------------
  const toggleSlot = useCallback(
    async (slot) => {
      if (!token) return;
      setLoading(true);

      try {
        const res = await fetch(`${API_BASE}/medecin/blocages/toggle`, {
          method: "POST",
          headers,
          body: JSON.stringify({ debut: slot.debutIso, fin: slot.finIso }),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setError(data.msg || "Erreur toggle");
          return;
        }

        await fetchBlocagesJour(date);
        await fetchAllBlocages();
      } catch (e) {
        setError(e?.message || "Impossible de contacter le serveur.");
      } finally {
        setLoading(false);
      }
    },
    [date, fetchAllBlocages, fetchBlocagesJour, headers, token]
  );

  // -------------------- Date picker UX --------------------
  const dateInputRef = useRef(null);

  const openDatePicker = () => {
    if (dateInputRef.current?.showPicker) dateInputRef.current.showPicker();
    else dateInputRef.current?.click();
  };

  const refreshDay = useCallback(async () => {
    try {
      setLoading(true);
      await fetchBlocagesJour(date);
      await fetchAllBlocages();
    } catch (e) {
      setError(e?.message || "Impossible de contacter le serveur.");
    } finally {
      setLoading(false);
    }
  }, [date, fetchAllBlocages, fetchBlocagesJour]);

  // -------------------- Render --------------------
  return (
    <div className="adminPanel blocagesCompact">
      <div className="adminPanelHeader blocagesHeader">
       

        <div className="blocagesHeaderActions">
        <button
          className="btnSecondary"
          onClick={async () => {
            await refreshDay();
            showToast("success", "Blocages sont à jour");
          }}
          disabled={loading}
        >
            {loading ? "Chargement..." : "Rafraîchir"}
          </button>

         
        </div>
      </div>

      {error && <div className="adminError"> {error}</div>}

      {/* ✅ Date card */}
      <div className="adminCard adminCardSpan blocagesDateCardCompact">
        <div className="blocagesDateCardLeft">
        <div className="blocagesDateLabel">
          <FaCalendarAlt style={{ marginRight: "6px" }} />
          Date sélectionnée 
        </div>  <br/>
       <div className="blocagesDateValue">{formatLongFr(date)}</div>
        </div>

        <div className="blocagesDateActions">
          <input
            ref={dateInputRef}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="blocagesHiddenDateInput"
            aria-hidden="true"
            tabIndex={-1}
          />
            <button
              className="btnPrimary btnCompact"
              onClick={openDatePicker}
              disabled={loading}
            >
              <FaCalendarAlt style={{ marginRight: "8px" }} />
              Changer la date
            </button>

          <button className="btnSecondary btnCompact" onClick={() => setDate(todayYYYYMMDD())} disabled={loading}>
            Aujourd’hui
          </button>
        </div>
      </div>

     {/* 🔴 Blocages d’aujourd’hui */}
        {todayBlocages.length > 0 && (
          <div className="adminCard adminCardSpan blocagesSection">
            <div className="adminCardHeader">
              <div className="adminCardTitle">Blocages d’aujourd’hui</div>
              <div className="adminCardDesc">
                {todayBlocages.length} en cours
              </div>
            </div>

            <div className="blocagesList">
              {todayBlocages.map((b) => {
                const debutDate = new Date(b.debut);
                const finDate = new Date(b.fin);

                const dateParis = debutDate.toLocaleDateString("fr-FR", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  timeZone: "Europe/Paris",
                });

                const heureDebutParis = debutDate.toLocaleTimeString("fr-FR", {
                  hour: "2-digit",
                  minute: "2-digit",
                  timeZone: "Europe/Paris",
                });

                const heureFinParis = finDate.toLocaleTimeString("fr-FR", {
                  hour: "2-digit",
                  minute: "2-digit",
                  timeZone: "Europe/Paris",
                });

                const isoDateParis = debutDate.toLocaleDateString("sv-SE", {
                  timeZone: "Europe/Paris",
                });

                return (
                  <div className="blocageItem blocageItemToday" key={b.id}>
                    <div className="blocageItemLeft">
                      <div className="blocageTime">
                        {dateParis} {heureDebutParis} → {heureFinParis}
                      </div>
                      <div className="blocageMotif">
                        {b.motif ? b.motif : "Sans motif"}
                      </div>
                    </div>

                    <button
                      className="btnSecondary btnCompact"
                      onClick={() => setDate(isoDateParis)}
                      disabled={loading}
                    >
                      Voir
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      {/* 🟦 Créneaux du jour */}
      <div className="adminCard adminCardSpan blocagesSection">
        <div className="adminCardHeader">
          <div className="adminCardTitle">Créneaux du {date}</div>
          <div className="adminCardDesc">
            {slots.length ? `${slots.length} créneau(x)` : "Aucun créneau (pas de dispo ce jour)"}
          </div>
        </div>

        {slots.length ? (
          <div className="blocagesSlots">
            {slots.map((s) => (
              <div className="slotItem" key={s.key}>
                <div className="slotLeft">
                  <div className="slotLabel">
                    <span className="slotDot" aria-hidden="true">
                    {s.blocked ? (
                      <FaLock color="#d32f2f" title="Créneau bloqué" />
                    ) : (
                      <FaUnlock color="#2e7d32" title="Créneau disponible" />
                    )}
                    </span>
                    {s.label}
                  </div>
                  <div className="slotStatus">{s.blocked ? "Bloqué" : "Disponible"}</div>
                </div>

                <button
                  className={`${s.blocked ? "btnDangerOutline" : "btnPrimary"} btnCompact`}
                  onClick={() => toggleSlot(s)}
                  disabled={loading}
                >
                  {s.blocked ? "Débloquer" : "Bloquer"}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="adminPlaceholder">
            <div className="adminPlaceholderTitle">Aucun créneau</div>
            <ul className="adminList">
              <li>Ajoutez une disponibilité hebdomadaire pour ce jour dans “Mes disponibilités”.</li>
            </ul>
          </div>
        )}
      </div>

      {/* 🟡 Blocages à venir */}
{upcomingBlocages.length > 0 && (
  <div className="adminCard adminCardSpan blocagesSection">
    <div className="adminCardHeader">
      <div className="adminCardTitle">Blocages à venir</div>
      <div className="adminCardDesc">
        {upcomingBlocages.length} prochain(s)
      </div>
    </div>

    <div className="blocagesList">
      {upcomingBlocages.map((b) => {
        const debutDate = new Date(b.debut);
        const finDate = new Date(b.fin);

        const dateParis = debutDate.toLocaleDateString("fr-FR", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          timeZone: "Europe/Paris",
        });

        const heureDebutParis = debutDate.toLocaleTimeString("fr-FR", {
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "Europe/Paris",
        });

        const heureFinParis = finDate.toLocaleTimeString("fr-FR", {
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "Europe/Paris",
        });

        const isoDateParis = debutDate.toLocaleDateString("sv-SE", {
          timeZone: "Europe/Paris",
        });

        return (
          <div className="blocageItem" key={b.id}>
            <div className="blocageItemLeft">
              <div className="blocageTime">
                {dateParis} {heureDebutParis} → {heureFinParis}
              </div>
              <div className="blocageMotif">
                {b.motif ? b.motif : ""}
              </div>
            </div>

            <button
              className="btnSecondary btnCompact"
              onClick={() => setDate(isoDateParis)}
              disabled={loading}
            >
              Voir
            </button>
          </div>
        );
      })}
    </div>
  </div>
)}

{toast.msg && (
  <div className={`adminToast ${toast.type}`}>
    {toast.msg}
  </div>
)}
    </div>
  );
}